import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { X, Send, ChevronDown, ChevronRight, CheckSquare, HelpCircle } from 'lucide-react';
import { saveAssessmentResponse, savePersonalAssessmentResponse } from '../services/api';
import logger from '../utils/logger';

const QuestionModal = ({ question, assessmentId, onClose, onSave, isReadOnly = false, assessmentType = 'business' }) => {
  const navigate = useNavigate();
  const [selectedValue, setSelectedValue] = useState(null);
  const [comments, setComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [showRuleOfThumb, setShowRuleOfThumb] = useState(false);
  const [showConsiderations, setShowConsiderations] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [noteHistory, setNoteHistory] = useState([]);
  const [showHelpModal, setShowHelpModal] = useState(false);

  useEffect(() => {
    if (question) {
      setSelectedValue(question.answer_value);
      // Parse comments as JSON for note history, or convert old format
      try {
        const parsedHistory = question.comments ? JSON.parse(question.comments) : [];
        setNoteHistory(Array.isArray(parsedHistory) ? parsedHistory : [{ text: question.comments, timestamp: new Date().toISOString() }]);
      } catch {
        // If comments is not JSON, convert old format to new format
        setNoteHistory(question.comments ? [{ text: question.comments, timestamp: new Date().toISOString() }] : []);
      }
      setComments(question.comments || '');
    }
  }, [question]);

  const handleSave = async () => {
    if (selectedValue === null) return;

    setSaving(true);
    try {
      const responseData = {
        assessment_id: assessmentId,
        question_id: question.question_id,
        category: question.category,
        subject: question.subject,
        question_text: question.question_text,
        answer_value: selectedValue,
        answer_text: getAnswerText(selectedValue, question.scale_type),
        comments: JSON.stringify(noteHistory),
        rule_of_thumb: question.rule_of_thumb,
        considerations: question.considerations,
        related_task: question.related_task
      };

      // Use the correct save function based on assessment type
      const result = assessmentType === 'personal'
        ? await savePersonalAssessmentResponse(responseData)
        : await saveAssessmentResponse(responseData);

      if (result.success) {
        if (onSave) {
          await onSave(responseData);
        }
        onClose();
      } else {
        alert('Failed to save response');
      }
    } catch (error) {
      logger.error('Error saving question response', { error, questionId: question.id, assessmentId });
      alert('Error saving response');
    }
    setSaving(false);
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const updatedHistory = [
      ...noteHistory,
      {
        text: newNote,
        timestamp: new Date().toISOString()
      }
    ];

    setNoteHistory(updatedHistory);
    setNewNote('');
  };

  const handleEditNote = (index) => {
    setEditingNoteIndex(index);
    setEditingNoteText(noteHistory[index].text);
  };

  const handleSaveEditedNote = (index) => {
    if (!editingNoteText.trim()) return;

    const updatedHistory = [...noteHistory];
    updatedHistory[index] = {
      ...updatedHistory[index],
      text: editingNoteText,
      lastEdited: new Date().toISOString()
    };

    setNoteHistory(updatedHistory);
    setEditingNoteIndex(null);
    setEditingNoteText('');
  };

  const handleCancelEdit = () => {
    setEditingNoteIndex(null);
    setEditingNoteText('');
  };

  const getAnswerText = (value, scaleType = 'comparative') => {
    if (value === 0) return 'N/A - Not Applicable';

    if (scaleType === 'documentation') {
      const docLabels = {
        1: "I Don't Know What You're Talking About",
        2: "I've Heard About It, But It's Not Clear",
        3: "It's Clear, But Not Documented",
        4: "It's Clear & Documented, But Not Transferable",
        5: "It's Clear, Documented, & Transferable",
        6: "It's Perfect - No Improvement Possible"
      };
      return docLabels[value] || '';
    }

    // Comparative/strength questions (default)
    const strengthLabels = {
      1: 'Weaker Than All Others',
      2: 'Weaker Than Most',
      3: 'Weak, But Gaining Strength',
      4: 'Strong, And Getting Stronger',
      5: 'Stronger Than Most',
      6: 'Stronger Than All Others'
    };
    return strengthLabels[value] || '';
  };

  const getButtonColor = (value, isSelected) => {
    if (isSelected) {
      const selectedColors = {
        0: 'bg-gray-600 border-gray-800 text-white shadow-lg',
        1: 'bg-red-700 border-red-900 text-white shadow-lg',
        2: 'bg-orange-600 border-orange-800 text-white shadow-lg',
        3: 'bg-yellow-600 border-yellow-800 text-white shadow-lg',
        4: 'bg-blue-700 border-blue-900 text-white shadow-lg',
        5: 'bg-green-700 border-green-900 text-white shadow-lg',
        6: 'border-2 text-white shadow-lg'
      };

      // Custom gold color for score 6
      if (value === 6) {
        return selectedColors[6] + ' ' + 'bg-[#a07d5a] border-[#8b6f47]';
      }

      return selectedColors[value] || selectedColors[0];
    }

    const unselectedColors = {
      0: 'bg-gray-100 border-gray-400 text-gray-800 hover:border-gray-500 hover:bg-gray-200',
      1: 'bg-red-100 border-red-400 text-red-800 hover:border-red-500 hover:bg-red-200',
      2: 'bg-orange-100 border-orange-400 text-orange-800 hover:border-orange-500 hover:bg-orange-200',
      3: 'bg-yellow-100 border-yellow-400 text-yellow-800 hover:border-yellow-500 hover:bg-yellow-200',
      4: 'bg-blue-100 border-blue-400 text-blue-800 hover:border-blue-500 hover:bg-blue-200',
      5: 'bg-green-100 border-green-400 text-green-800 hover:border-green-500 hover:bg-green-200',
      6: 'border-2 hover:border-2'
    };

    // Custom gold color for score 6 (unselected)
    if (value === 6) {
      return unselectedColors[6] + ' ' + 'bg-[#e8d4bb] border-[#a07d5a] text-[#6b5333] hover:bg-[#d4c1a3] hover:border-[#8b6f47]';
    }

    return unselectedColors[value] || unselectedColors[0];
  };

  if (!question) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-full sm:max-w-3xl md:max-w-4xl lg:max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Responsive */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-200 gap-3">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Update Score</h2>
            <button
              onClick={() => setShowHelpModal(true)}
              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 transition p-1.5 rounded-lg hover:bg-blue-50 whitespace-nowrap min-h-[44px]"
              title="Learn more about Clear, Documented, & Transferable"
            >
              <HelpCircle size={18} />
              <span className="text-xs font-medium hidden sm:inline">What does "Clear, Documented, & Transferable" mean?</span>
              <span className="text-xs font-medium sm:hidden">Help</span>
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition min-w-[44px] min-h-[44px] flex items-center justify-center absolute top-4 right-4 sm:relative sm:top-0 sm:right-0"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content - Responsive */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          <div className="space-y-3">
            {/* Question Section - Compact */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-4 text-white">
              <div className="space-y-2">
                <div>
                  <h3 className="text-xs font-medium text-white/70 uppercase tracking-wide mb-1">Subject</h3>
                  <p className="text-lg font-semibold leading-snug">{question.subject}</p>
                </div>

                <div className="pt-2 border-t border-white/20">
                  <h3 className="text-xs font-medium text-white/70 uppercase tracking-wide mb-1">Question</h3>
                  <p className="text-sm leading-snug text-white/90">{question.question_text}</p>
                </div>
              </div>
            </div>

            {/* Button Scale 0-6 - Compact */}
            <div>
              <div className="grid grid-cols-7 gap-1.5">
                {[0, 1, 2, 3, 4, 5, 6].map((value) => {
                  const isSelected = selectedValue === value;
                  const shortLabels = {
                    0: 'N/A',
                    1: '1',
                    2: '2',
                    3: '3',
                    4: '4',
                    5: '5',
                    6: '6'
                  };

                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => !isReadOnly && setSelectedValue(value)}
                      disabled={isReadOnly}
                      className={`py-2 px-1 rounded-md border-2 transition-all text-center ${
                        getButtonColor(value, isSelected)
                      } ${isSelected ? 'scale-105 shadow-md' : isReadOnly ? 'cursor-not-allowed opacity-75' : 'hover:scale-102'}`}
                      title={getAnswerText(value, question.scale_type)}
                    >
                      <div className="text-lg font-bold">{shortLabels[value]}</div>
                    </button>
                  );
                })}
              </div>
              {selectedValue !== null && (
                <p className="mt-2 text-xs text-gray-600 text-center font-medium">
                  {getAnswerText(selectedValue, question.scale_type)}
                </p>
              )}
            </div>

            {/* Guidance Section - Compact & Collapsible */}
            <div className="space-y-2">
              {/* Rule of Thumb - Compact */}
              {question.rule_of_thumb && (
                <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
                  <button
                    onClick={() => setShowRuleOfThumb(!showRuleOfThumb)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-base">💡</span>
                      <span className="text-sm font-semibold text-gray-900">Rule of Thumb</span>
                    </div>
                    {showRuleOfThumb ? (
                      <ChevronDown size={16} className="text-gray-500" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-500" />
                    )}
                  </button>
                  {showRuleOfThumb && (
                    <div className="px-3 pb-3 pt-2 bg-blue-50/50 border-t border-gray-200">
                      <p className="text-xs text-gray-700 leading-relaxed">{question.rule_of_thumb}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Considerations - Compact */}
              {question.considerations && (
                <div className="border border-gray-200 rounded-md overflow-hidden bg-white">
                  <button
                    onClick={() => setShowConsiderations(!showConsiderations)}
                    className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-base">📋</span>
                      <span className="text-sm font-semibold text-gray-900">Why This Matters</span>
                    </div>
                    {showConsiderations ? (
                      <ChevronDown size={16} className="text-gray-500" />
                    ) : (
                      <ChevronRight size={16} className="text-gray-500" />
                    )}
                  </button>
                  {showConsiderations && (
                    <div className="px-3 pb-3 pt-2 bg-green-50/50 border-t border-gray-200">
                      {Array.isArray(question.considerations) ? (
                        <ul className="list-disc list-inside space-y-1 text-xs text-gray-700 leading-relaxed">
                          {question.considerations.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-gray-700 leading-relaxed">{question.considerations}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Related Task - Compact */}
            {question.related_task && (
              <div className="bg-yellow-50 rounded-md p-3 border border-yellow-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-1">
                  <span>✓</span>
                  <span>Related Task</span>
                </h3>
                <p className="text-xs text-gray-700 leading-relaxed">{question.related_task}</p>
                <p className="text-xs text-gray-500 mt-1">
                  This task will be created if your score is low
                </p>
              </div>
            )}

            {/* Notes History - Compact */}
            <div className="border border-gray-200 rounded-md p-3 bg-white">
              <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                <span>📝</span>
                <span>Notes History</span>
              </h3>

              {/* Add New Note */}
              {!isReadOnly && (
                <div className="mb-3">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows="2"
                    placeholder="Add a new note..."
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                    className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Note
                  </button>
                </div>
              )}

              {/* Notes List */}
              {noteHistory.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {noteHistory.map((note, index) => (
                    <div key={index} className="bg-gray-50 rounded-md p-2 border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-semibold text-gray-500">
                          {note.lastEdited ? (
                            <>
                              Last edited: {new Date(note.lastEdited).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </>
                          ) : (
                            new Date(note.timestamp).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })
                          )}
                        </span>
                        {!isReadOnly && editingNoteIndex !== index && (
                          <button
                            onClick={() => handleEditNote(index)}
                            className="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      {editingNoteIndex === index ? (
                        <div>
                          <textarea
                            value={editingNoteText}
                            onChange={(e) => setEditingNoteText(e.target.value)}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none mb-1.5"
                            rows="3"
                          />
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => handleSaveEditedNote(index)}
                              className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-700 whitespace-pre-wrap">{note.text}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic text-center py-3">No notes yet. Add your first note above.</p>
              )}
            </div>

            {/* Question Tags - Small but visible */}
            <div className="border-t border-gray-200 pt-3 mt-4">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tags:</span>

                {/* Activity Type Tag */}
                {question.activity_type && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    <span className="mr-1">⚡</span>
                    {question.activity_type}
                  </span>
                )}

                {/* Intangible Asset Type Tag */}
                {question.intangible_asset_type && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                    <span className="mr-1">💎</span>
                    {question.intangible_asset_type}
                  </span>
                )}

                {/* Impact Type Tags - Readiness and/or Attractiveness */}
                {question.impacts_readiness && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    <span className="mr-1">🎯</span>
                    Readiness
                  </span>
                )}
                {question.impacts_attractiveness && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                    <span className="mr-1">✨</span>
                    Attractiveness
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Save Button - Compact */}
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900 font-semibold transition"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate('/task-manager', { state: { questionId: question.question_id, assessmentType } })}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-md hover:bg-indigo-100 transition font-medium border border-indigo-200"
              >
                <CheckSquare size={16} />
                <span>View Related Tasks</span>
              </button>
            </div>
            {!isReadOnly && (
              <button
                onClick={handleSave}
                disabled={selectedValue === null || saving}
                className="px-6 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-1.5 font-semibold"
              >
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                {!saving && <span>💾</span>}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Help Modal for "Clear, Documented, & Transferable" - Responsive */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Modal Header - Responsive */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 pr-2">
                Understanding "Clear, Documented, & Transferable"
              </h2>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-gray-400 hover:text-gray-600 transition min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Content - Responsive */}
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Introduction */}
              <div className={`border-l-4 p-4 rounded ${
                assessmentType === 'personal'
                  ? 'bg-purple-50 border-purple-500'
                  : 'bg-blue-50 border-blue-500'
              }`}>
                <p className="text-gray-700 leading-relaxed">
                  {assessmentType === 'personal' ? (
                    <>
                      These three attributes represent the <strong>gold standard</strong> for personal planning,
                      systems, and readiness that ensure smooth life transitions. Strong preparation in your personal
                      life protects your family and provides peace of mind during exit and beyond.
                    </>
                  ) : (
                    <>
                      These three attributes represent the <strong>gold standard</strong> for business processes,
                      systems, and operations that maximize transferable value. Buyers pay premium prices for
                      businesses that can run independently of the current owner.
                    </>
                  )}
                </p>
              </div>

              {/* Clear */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="bg-green-100 text-green-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                    1
                  </span>
                  Clear
                </h3>
                <div className="ml-11 space-y-2">
                  <p className="text-gray-700">
                    <strong>Definition:</strong> {assessmentType === 'personal'
                      ? 'Plans, responsibilities, and intentions are unambiguous, easy to understand, and clearly communicated to family and advisors.'
                      : 'Processes and responsibilities are unambiguous, easy to understand, and consistently executed.'}
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>Defined estate plan with explicit beneficiary designations</li>
                          <li>Clear financial goals and retirement vision</li>
                          <li>Explicit family roles and responsibilities</li>
                          <li>Well-articulated values and legacy intentions</li>
                        </>
                      ) : (
                        <>
                          <li>Step-by-step sales process that anyone can follow</li>
                          <li>Defined roles with explicit job descriptions</li>
                          <li>Straightforward pricing methodology</li>
                          <li>Transparent financial reporting with clear KPIs</li>
                        </>
                      )}
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-2">Red Flags (Not Clear):</p>
                    <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>"My family knows what I want" (but nothing is explicitly stated)</li>
                          <li>Vague intentions with no specific plans</li>
                          <li>Family members have different understandings of your wishes</li>
                        </>
                      ) : (
                        <>
                          <li>"We just figure it out as we go"</li>
                          <li>Tribal knowledge that only veterans understand</li>
                          <li>Inconsistent execution across team members</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Documented */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                    2
                  </span>
                  Documented
                </h3>
                <div className="ml-11 space-y-2">
                  <p className="text-gray-700">
                    <strong>Definition:</strong> {assessmentType === 'personal'
                      ? 'Written plans, legal documents, and instructions exist that can be referenced, updated, and used by family and advisors.'
                      : 'Written procedures, policies, and systems exist that can be referenced, updated, and used for training.'}
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>Executed estate planning documents (wills, trusts, powers of attorney)</li>
                          <li>Written financial plan with investment policy statement</li>
                          <li>Healthcare directives and end-of-life wishes documented</li>
                          <li>Family governance documents or family constitution</li>
                          <li>Asset inventory with account details and access information</li>
                        </>
                      ) : (
                        <>
                          <li>Standard Operating Procedures (SOPs) for key processes</li>
                          <li>Employee handbook and training manuals</li>
                          <li>Written contracts and agreements with customers/vendors</li>
                          <li>Organizational charts and process flowcharts</li>
                          <li>Knowledge base or wiki for common issues</li>
                        </>
                      )}
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-2">Red Flags (Not Documented):</p>
                    <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>"It's all in my head" or "I'll tell my spouse later"</li>
                          <li>No updated will or estate documents</li>
                          <li>Verbal agreements with no legal backing</li>
                        </>
                      ) : (
                        <>
                          <li>"It's all in my head" or "I'll show the new owner"</li>
                          <li>No written procedures or manuals</li>
                          <li>Reliance on verbal instructions</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Transferable */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <span className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                    3
                  </span>
                  Transferable
                </h3>
                <div className="ml-11 space-y-2">
                  <p className="text-gray-700">
                    <strong>Definition:</strong> {assessmentType === 'personal'
                      ? 'Responsibilities, knowledge, and relationships can be effectively handed off to family members, heirs, or advisors without significant disruption or loss of continuity.'
                      : 'The system can be effectively handed off to a new person without significant loss of efficiency or quality. The business doesn\'t depend on any single individual.'}
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                    <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>Spouse or trusted family member can access accounts and understand finances</li>
                          <li>Advisory team knows your family and can continue serving them</li>
                          <li>Executors and trustees are prepared and informed</li>
                          <li>Children understand family values, wealth philosophy, and expectations</li>
                          <li>Important relationships are introduced to family members</li>
                        </>
                      ) : (
                        <>
                          <li>New employees can be onboarded using documentation</li>
                          <li>Customer relationships aren't tied to one person</li>
                          <li>Systems and software can be accessed by others</li>
                          <li>Vendor relationships are company-based, not personal</li>
                          <li>Management team can operate without owner</li>
                        </>
                      )}
                    </ul>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="font-semibold text-red-800 mb-2">Red Flags (Not Transferable):</p>
                    <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                      {assessmentType === 'personal' ? (
                        <>
                          <li>You're the only one who knows where things are or how things work</li>
                          <li>Advisors don't know your family or long-term intentions</li>
                          <li>Passwords and account access known only to you</li>
                          <li>Family would struggle to manage affairs in your absence</li>
                        </>
                      ) : (
                        <>
                          <li>Owner is the only one who can perform critical functions</li>
                          <li>Key relationships would leave with owner</li>
                          <li>Passwords, accounts only accessible to owner</li>
                          <li>Long training period required for handoff</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Why It Matters */}
              <div className={`border-l-4 p-4 rounded ${
                assessmentType === 'personal'
                  ? 'bg-purple-50 border-purple-500'
                  : 'bg-yellow-50 border-yellow-500'
              }`}>
                <h4 className={`font-bold mb-2 ${
                  assessmentType === 'personal' ? 'text-purple-900' : 'text-yellow-900'
                }`}>
                  💡 {assessmentType === 'personal' ? 'Why This Matters for Your Family' : 'Why This Matters for Exit Value'}
                </h4>
                <p className={`text-sm leading-relaxed ${
                  assessmentType === 'personal' ? 'text-purple-800' : 'text-yellow-800'
                }`}>
                  {assessmentType === 'personal' ? (
                    <>
                      Clear, documented, and transferable personal planning <strong>protects your family</strong> and
                      provides peace of mind during life transitions. Families with strong preparation experience{' '}
                      <strong>significantly less stress and conflict</strong> during difficult times. Without proper
                      planning, loved ones often face unnecessary confusion, delays, and financial complications when
                      they're most vulnerable.
                    </>
                  ) : (
                    <>
                      Buyers pay <strong>2-4x more</strong> for businesses with clear, documented, and transferable
                      systems because they reduce risk and enable a smooth transition. A business that depends on
                      the owner's personal knowledge and relationships is worth significantly less than one that
                      operates like a well-oiled machine.
                    </>
                  )}
                </p>
              </div>

              {/* Scoring Guide */}
              <div className="border-t pt-4">
                <h4 className="font-bold text-gray-900 mb-3">Assessment Scoring Scale (0-6):</h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center bg-[#c49e73] text-white border-2 border-[#a07d5a]">6</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Exemplary & Optimized</p>
                      <p className="text-xs text-gray-600">Fully clear, comprehensively documented, and seamlessly transferable. Best-in-class execution with no meaningful improvements needed.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">5</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Clear, Documented & Transferable</p>
                      <p className="text-xs text-gray-600">Meets all three core criteria with strong execution. Minor refinements possible but fundamentally sound and ready for transition.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">4</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Clear & Documented, Not Yet Transferable</p>
                      <p className="text-xs text-gray-600">Well-defined and properly documented, but remains dependent on specific individuals. Requires knowledge transfer planning to reduce key person risk.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">3</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Clear But Lacking Documentation</p>
                      <p className="text-xs text-gray-600">Processes are understood and consistently executed, but not formally written down. Institutional knowledge exists but isn't captured in accessible formats.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">2</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Conceptually Understood, Poorly Defined</p>
                      <p className="text-xs text-gray-600">You recognize the importance and generally understand the concept, but execution is inconsistent, unclear, or varies by person. Needs structure and standardization.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-red-100 text-red-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">1</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Unfamiliar or Absent</p>
                      <p className="text-xs text-gray-600">This area is not currently addressed in your business, or you're unclear on what the question is asking. Represents a significant gap requiring immediate attention.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">0</div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">Not Applicable (N/A)</p>
                      <p className="text-xs text-gray-600">This question does not apply to your business model or industry. These responses are excluded from your final score calculation.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setShowHelpModal(false)}
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

QuestionModal.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    question_id: PropTypes.number,
    question_text: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    rule_of_thumb: PropTypes.string,
    guidance: PropTypes.string,
    answer_value: PropTypes.number,
    comments: PropTypes.string
  }).isRequired,
  assessmentId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  assessmentType: PropTypes.oneOf(['business', 'personal'])
};

QuestionModal.defaultProps = {
  isReadOnly: false,
  assessmentType: 'business'
};

export default QuestionModal;
